import { ListSourcedValue, Option, SourcedValue } from '../option.js';
import { optionIdSymbol, typeSymbol } from '../engine-impl.js';
import { pathToRoot, PropertyValue, SourceType } from './PropertyPathWalker.js';
import { BigNumber, ListType, MLString, ObjectType, Property, SystemType, TypeKind, TypeReference, UnionType } from '../types.js';
import { Schema } from '../schema.js';
import { SourceUtils } from './SourceUtils.js';
import { IEntitySearchResult, PModel } from '../engine.js';
import { ModelSystemProperties } from './ModelSystemProperties.js';
import { ModelError, ModelErrorCode } from '../errors/ModelError.js';
import { Constraints } from '../constraint.js';


interface IPrototypeTraverser {
  map<T>(fn: IPrototypeVisitor): T
}

interface IPrototypeVisitor {
  buildPrimitive(propValue: PropertyValue): any;

  buildObject(properties: PropertyValue[], objectPropValue: PropertyValue): any;
}

export class SimpleVisitor implements IPrototypeVisitor {
  constructor(protected schema: Schema) {
  }

  buildPrimitive(propValue: PropertyValue): any {
    switch (propValue.property.type.toLowerCase()) {
      case SystemType.MLString.toLowerCase():
        if (propValue.value instanceof MLString) {
          return propValue.value;
        }
        return new MLString(propValue.value);
      case SystemType.BigNumber.toLowerCase():
        if (propValue.value instanceof BigNumber) {
          return propValue.value;
        }
        return new BigNumber(propValue.value);
      default:
        return propValue.value;
    }
  }

  buildObject(properties: PropertyValue[], objectPropValue: PropertyValue): PModel {
    let object = {};
    for (let prop of properties) {
      let value = prop.value;
      if (value == null && prop.property.opts.default != null) {
        value = prop.property.opts.default;
      }
      object[prop.property.name] = value;
      const sourceUtils = new SourceUtils(this.schema);
      const originValue = objectPropValue.value[prop.property.name];
      if (sourceUtils.isSourceProvider(prop)) {
        if (sourceUtils.isSourceProviderHasSources(objectPropValue.value as PModel, prop.property.name)) {
          ModelSystemProperties.addSourceToModel(object as PModel, prop.property.name, ModelSystemProperties.getSourceFromModel(objectPropValue.value as PModel, prop.property.name));
        } else {
          const providerType = sourceUtils.sourceProviderType(prop);
          if (providerType == SourceType.Single && !(originValue instanceof SourcedValue)) {
            throw new Error(`SingleSV: Property:${pathToRoot(prop)} should have sourced values`)
          }
          if (providerType == SourceType.ListCommon && !(originValue instanceof SourcedValue)) {
            throw new Error(`SingleSV: Property:${pathToRoot(prop)} should have sourced values`)
          }
          if (providerType == SourceType.ListCommonForNested && !(originValue instanceof ListSourcedValue)) {
            throw new Error(`CommonSVWithNested: Property:${pathToRoot(prop)} should have sourced values`)
          }
          if (providerType == SourceType.ListSpecific && originValue.find(item => !(item instanceof SourcedValue))) {
            throw new Error(`SpecificSV: Items inside property:${pathToRoot(prop)} should have sourced values`);
          }
          sourceUtils.setSourcesToModel(object as PModel, prop.property.name, originValue);
        }
      }
    }
    object[typeSymbol] = objectPropValue.property.type;
    // Put option id into model
    if (objectPropValue.value._id) {
      SourceUtils.setOptionId(object, objectPropValue.value._id);
    }
    return object as PModel;
  }
}

export class ValidationVisitor extends SimpleVisitor {
  buildObject(properties: PropertyValue[], objectPropValue: PropertyValue): PModel {
    for (let prop of properties) {
      if (prop.property.opts.constraints.find(constant => constant.name === Constraints.Required) && prop.value === undefined) {
        throw new Error(`Required property (${prop.property.name}) is not defined`);
      }
    }

    return super.buildObject(properties, objectPropValue);
  }
}

export class ComponentSearchVisitor<T> extends SimpleVisitor {
  searchResult: IEntitySearchResult<T>[] = [];

  constructor(schema: Schema, private components: TypeReference[]) {
    super(schema);
  }

  buildPrimitive(propValue: PropertyValue): any {
    return propValue.value;
  }

  buildObject(properties: PropertyValue[], objectPropValue: PropertyValue): PModel {
    const entity = objectPropValue.value;
    const typeRef = objectPropValue.property.type;
    const type = this.schema.getType(typeRef);

    if (type instanceof ObjectType && type.components.length > 0) {
      // Type contains all components
      if (this.components.every(component => type.components.includes(component))) {
        this.searchResult.push({
          entity,
          path: pathToRoot(objectPropValue)
        })
      }
    }
    return entity;
  }
}

export class PrototypeTraverser implements IPrototypeTraverser {
  constructor(private schema: Schema, private typeRef: TypeReference, private prototype: any) {
  }

  map(visitor: IPrototypeVisitor): any {
    const rootProperty = new Property('$', this.typeRef);
    const rootPropValue = { parent: undefined, property: rootProperty, value: this.prototype };
    const model = this._map(rootPropValue, visitor);
    return model;
  }

  private _map(propValue: PropertyValue, visitor: IPrototypeVisitor) {
    if (propValue.value === undefined) {
      return;
    }
    const propertyMappedValue = SourceUtils.getInnerValue(propValue.value);

    let type = this.schema.getType(propValue.property.type);
    switch (type.kind) {
      case TypeKind.Primitive:
        return visitor.buildPrimitive(propValue);
      case TypeKind.List:
        if (!Array.isArray(propertyMappedValue)) {
          throw new Error(`${propValue.property.name} should be a List`);
        } else {
          if (!(type instanceof ListType)) throw new Error('Incorrect list type');
          const itemType = type.elementType;

          const items = propertyMappedValue.map((v, i) => {
            const itemPropValue: PropertyValue = { value: v, property: new Property(i.toString(), itemType), parent: propValue };
            return this._map(itemPropValue, visitor);
          });
          items[typeSymbol] = propValue.property.type;
          return items;
        }

      case TypeKind.Enum:
        return visitor.buildPrimitive(propValue);
      case TypeKind.Union:
        if (!(type instanceof UnionType)) {
          throw new ModelError(ModelErrorCode.UNION_TYPE_VALUE_EXPECTED, `Type '${propValue.property.type}' should be an union type`);
        }
        const unwrappedValue = propValue.parent?.value[propValue.property.name];
        let runtimeTypeRef: TypeReference = null;

        //Option
        if (!runtimeTypeRef && unwrappedValue instanceof Option) {
          runtimeTypeRef = unwrappedValue.type.typeRef;
        }
        //Explicit type
        if (!runtimeTypeRef) {
          runtimeTypeRef = ModelSystemProperties.getExplicitType(propValue.value);
        }

        //Implicit type
        if (!runtimeTypeRef) {
          runtimeTypeRef = ModelSystemProperties.getTypeRef(unwrappedValue);
        }

        // PModel
        if (!runtimeTypeRef && ModelSystemProperties.getTypeRef(propValue.parent.value)) {
          const parent = propValue.parent.value;
          runtimeTypeRef = ModelSystemProperties.getTypeRef(parent[propValue.property.name]);
        }

        if (!runtimeTypeRef) {
          const path = pathToRoot(propValue);
          throw new ModelError(ModelErrorCode.UNION_TYPE_VALUE_MISSING_RUNTIME_TYPE, `Cannot determine runtime type of ${type.name} for "${path}". Try to use Options or specify explicit type with _type:<Type>`)
        }

        let runtimeType = this.schema.getType(runtimeTypeRef);

        if (!runtimeType) {
          throw new Error(`Type ${runtimeTypeRef} not found`);
        }
        if (!type.types.find(t => t === runtimeTypeRef)) {
          throw new Error(`Type '${runtimeType.name}' is not in a list of ${type.name} union`)
        }
        type = runtimeType;
        propValue.property = { ...propValue.property, type: type.name };
      case TypeKind.Object:
        if (!(type instanceof ObjectType)) {
          throw new Error(`Type '${propValue.property.type}' should be an object type`);
        }
        if (propertyMappedValue) {
          const properties = [];
          const fullProperties = this.schema.getFullObjectTypeProperties(type);
          for (const property of fullProperties) {
            properties.push(property.name);
          }

          properties.sort().reverse();

          const objectProperties = [];
          while (properties.length > 0) {
            let currentPropertyName = properties.pop();
            if (!currentPropertyName) {
              continue;
            }
            let property = type.getProperty(currentPropertyName);
            if (!property) {
              throw new Error(`Property '${currentPropertyName}' not exist in type ${type.getTypeReference()}`);
            }
            // Property structure may be different than in type because of SourcedValue/Option.
            const value = SourceUtils.getInnerValue(propertyMappedValue[currentPropertyName]);
            //propValue.value[currentPropertyName]
            const itemPropValue: PropertyValue = { property: property, value: value, parent: propValue };
            // objectProperties[currentPropertyName] = this._map(itemPropValue, visitor);
            objectProperties.push({ property: property, value: this._map(itemPropValue, visitor), parent: propValue });
          }
          let obj = visitor.buildObject(objectProperties, propValue);
          if (propertyMappedValue && propertyMappedValue[optionIdSymbol] && obj) {
            obj[optionIdSymbol] = propertyMappedValue[optionIdSymbol];
          }
          return obj;
        }
    }
  }
}
